import { useState, useCallback, useEffect } from 'react';
import { SelectItemProps, Response } from '@ui/src';
import { useAppSelector } from '@store';
import { PspPartner } from '../../../types/psp-partner';
import { useLazyGetTransactionsPspPartnersQuery } from '../services';

interface ListResponse {
  data: Response<PspPartner>;
}

let timeout: NodeJS.Timeout;

const useGetPspPartnersAutocompleteList = (
  query?: string,
  getAll?: boolean,
) => {
  const [autocompleteList, setAutocompleteList] = useState<SelectItemProps[]>(
    [],
  );
  const [pspPartnerList, setPspPartnerList] = useState<PspPartner[]>([]);

  const { pspPartners } = useAppSelector(
    (state) => state.transactionsDictionaries,
  );

  const [getList, { isFetching, isLoading }] =
    useLazyGetTransactionsPspPartnersQuery();

  const getAutocompleteListAction = useCallback(
    async (search?: string) => {
      const results: SelectItemProps[] = [];

      const response = (await getList(
        `?take=40${search ? `&companyName=${search}` : ''}${query || ''}${
          getAll ? '&all=true' : ''
        }&active=true`,
      )) as ListResponse;

      if (response?.data?.data) {
        response.data.data.edges?.forEach((item) => {
          results.push({
            name: item.node.companyName,
            description: item.node.description,
            value: item.node.id,
          });
        });

        setPspPartnerList(
          response?.data?.data?.edges?.map((item) => item.node),
        );
      }

      setAutocompleteList(results);
    },
    [query],
  );

  const onSearch = (search: string) => {
    clearTimeout(timeout);

    timeout = setTimeout(() => {
      getAutocompleteListAction(search);
    }, 300);
  };

  useEffect(() => {
    if (!pspPartners) {
      getAutocompleteListAction();
    } else {
      setAutocompleteList(
        pspPartners?.map((item) => ({
          name: item.companyName,
          description: item.description,
          value: item.id,
        })),
      );

      setPspPartnerList(pspPartners);
    }
  }, [getAutocompleteListAction, pspPartners]);

  return {
    getAutocompleteListAction,
    autocompleteList,
    onSearch,
    isLoading: isLoading || isFetching,
    pspPartnerList,
  };
};

export default useGetPspPartnersAutocompleteList;
